import React, { useRef, useState } from 'react';
import { backdropClose } from '../utils/backdropClose';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ProfilePhotoUploadProps {
  /** The "own" photo for this viewer (gym-uploaded for gym; member-uploaded for member) */
  currentPhotoUrl?: string | null;
  /** Fallback photo uploaded by the other party */
  fallbackPhotoUrl?: string | null;
  /** Called with the cropped JPEG blob once user confirms */
  onUpload: (blob: Blob) => Promise<void>;
  uploading: boolean;
  /** Initials or single char to show when no photo exists */
  initials?: string;
  /** Avatar diameter in px (default 72) */
  size?: number;
  readOnly?: boolean;
  /** Visual variant: 'light' for white/gray background, 'dark' for zinc/black */
  variant?: 'light' | 'dark';
}

function centerAspectCrop(w: number, h: number) {
  return centerCrop(makeAspectCrop({ unit: '%', width: 90 }, 1, w, h), w, h);
}

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  currentPhotoUrl,
  fallbackPhotoUrl,
  onUpload,
  uploading,
  initials = '?',
  size = 72,
  readOnly = false,
  variant = 'light',
}) => {
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isEditing, setIsEditing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayPhoto = currentPhotoUrl || fallbackPhotoUrl;
  const isOwnPhoto = !!currentPhotoUrl;

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
        setIsEditing(true);
      });
      reader.readAsDataURL(e.target.files[0]);
      // Reset input so same file can be re-selected
      e.target.value = '';
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height));
  };

  const handleConfirm = async () => {
    if (!imgRef.current || !completedCrop) return;

    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height,
    );

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.9),
    );
    if (!blob) return;

    await onUpload(blob);
    setIsEditing(false);
  };

  const isDark = variant === 'dark';

  return (
    <>
      {/* Avatar trigger */}
      <div className="relative group" style={{ width: size, height: size }}>
        <div
          className={`w-full h-full rounded-full overflow-hidden flex items-center justify-center
            ${isDark
              ? 'bg-zinc-900 border-2 border-[#AAD816] shadow-[0_0_12px_rgba(170,216,22,0.2)]'
              : 'bg-white/20 border-2 border-white/60 shadow-lg'
            }`}
        >
          {displayPhoto ? (
            <img
              src={displayPhoto}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          ) : (
            <span
              className={`font-black select-none ${isDark ? 'text-white' : 'text-white'}`}
              style={{ fontSize: size * 0.38 }}
            >
              {initials.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        {/* Fallback badge — shown when displaying the other party's photo */}
        {!isOwnPhoto && displayPhoto && (
          <span
            className="absolute -bottom-1 -right-1 text-[9px] font-black uppercase rounded-full px-1 py-0.5 bg-white/80 text-gray-600 leading-none shadow"
            title="Photo uploaded by the other party"
          >
            {isDark ? 'gym' : 'member'}
          </span>
        )}

        {!readOnly && (
          <>
            <label
              htmlFor="profile-photo-input"
              className={`absolute inset-0 rounded-full flex items-center justify-center cursor-pointer
                opacity-0 group-hover:opacity-100 transition-opacity
                ${isDark ? 'bg-black/60' : 'bg-black/50'}`}
            >
              <span className="text-white font-black text-[10px] uppercase tracking-wider">Edit</span>
            </label>
            <input
              id="profile-photo-input"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onSelectFile}
            />
          </>
        )}

        {/* Upload spinner overlay */}
        {uploading && (
          <div className="absolute inset-0 rounded-full bg-black/70 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Crop modal */}
      {isEditing && imgSrc && (
        <div
          className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          {...backdropClose(() => setIsEditing(false))}
        >
          <div
            className="bg-zinc-950 border border-zinc-800 rounded-2xl max-w-lg w-full p-6 space-y-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-black text-white uppercase tracking-tight">
              Crop Profile Photo
            </h3>

            <div className="overflow-hidden rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center min-h-[200px]">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  alt="Crop"
                  src={imgSrc}
                  onLoad={onImageLoad}
                  className="max-h-[55vh] object-contain"
                />
              </ReactCrop>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 px-4 py-3 bg-zinc-800 text-zinc-300 font-black uppercase text-xs rounded-xl hover:bg-zinc-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={uploading || !completedCrop}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-black uppercase text-xs rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Save Photo'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfilePhotoUpload;
